import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Grid, Tooltip, Typography } from '@mui/material'
import SwitchToggle from 'components/form/SwitchToggle'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import { UseFormReturn } from 'react-hook-form'
import { ChallengeImageType, type ChallengeImageType as ImageType } from 'api/types'
import FilePicker from '../FilePicker'
import { useEffect, useState } from 'react'
import { sendErrorNotification } from 'utils/notifications'
import CropImageDialog from 'components/dialog/CropImageDialog'
import imageCompression from 'browser-image-compression'
import RichTextEditor from '../RichTextEditor'
import {
  confirmChallengeMediaUpload,
  deleteChallengeMedia,
  getChallengeMedia,
  requestChallengeMediaUpload,
} from 'store/challengesMedia/thunks'
import { useAppDispatch } from 'store'
import { RemoveIcon } from 'assets/icons/remove-icon'
import { resetChallengesMedia, selectChallengesMedia } from 'store/challengesMedia/slice'
import { useSelector } from 'react-redux'
import { UploadCloudIcon } from 'assets/icons/upload-cloud'

interface Props {
  form: UseFormReturn<any>
  challenge: Challenge | null
}

type ImageField = {
  name: string
  label: string
  suggestedWidth: number
  suggestedHeight: number
  maxFileSizeMB: number
  circular?: boolean
  logoWidth?: number
}

export default function ChallengeFormPhase5({ form, challenge }: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { control, watch } = form
  const [fileExtension, setFileExtension] = useState('')
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [imageInputData, setImageInputData] = useState('')
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({})
  const [currentImageField, setCurrentImageField] = useState<ImageField | null>(null)
  const [editingPreview, setEditingPreview] = useState<ImageField | null>(null)
  const { media: processedImages } = useSelector(selectChallengesMedia)
  const allImages = { ...processedImages, ...localPreviews }
  const isRequired = watch('terms.isRequired')
  const imageField = {
    name: ChallengeImageType.TERMS,
    label: intl.formatMessage({ id: 'challenges.optInLogo' }),
    suggestedWidth: 837,
    suggestedHeight: 264,
    maxFileSizeMB: 0.5,
  }
  const isUnsavedChallenge = challenge?.challengeId === '-1'

  useEffect(() => {
    setLocalPreviews({})
    dispatch(resetChallengesMedia())
  }, [])

  useEffect(() => {
    const loadImages = async () => {
      if (!challenge?.challengeId || challenge.challengeId === '-1') {
        return
      }
      try {
        await dispatch(
          getChallengeMedia({
            challengeId: challenge.challengeId,
            challengeImageType: ChallengeImageType.TERMS,
          }),
        ).unwrap()
      } catch {
        console.error(`Failed to load ${ChallengeImageType.TERMS} for challenge ${challenge.challengeId}`)
      }
    }
    loadImages()
  }, [challenge?.challengeId, dispatch])

  const onFilePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (!imageField) {
      return
    }

    const { files } = e.target

    if (files && files.length > 0) {
      const file = files[0]

      // File type validation
      if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
        sendErrorNotification('Invalid image file type. Has to be JPEG, JPG or PNG.')
        return
      }

      setFileExtension(file.type.replace(/(.*)\//g, ''))

      if (!challenge?.challengeId || challenge?.challengeId === '-1') {
        sendErrorNotification('Challenge ID is missing. Please save the challenge before uploading images.')
        return
      }

      // No crop for background
      setCropDialogOpen(true)
      setCurrentImageField(imageField)

      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImageInputData(reader.result as string)
      })
      reader.readAsDataURL(file)
    }
  }

  const renderImagePreview = (imageField: ImageField) => {
    const imageUrl = allImages[imageField.name]
    return (
      <ImagePreview
        $hasImage={!!imageUrl}
        $circular={imageField.circular}
        $aspectRatio={imageField.suggestedWidth / imageField.suggestedHeight}
      >
        {imageUrl ? (
          <>
            <img src={imageUrl} />

            {!form.formState.disabled && (
              <RemoveButton onClick={() => handleRemoveImage(imageField.name)}>
                <RemoveIcon width={24} height={24} fill="#0BB163" crossFill="white" />
              </RemoveButton>
            )}
          </>
        ) : (
          intl.formatMessage({ id: 'challenges.preview' }) || 'Preview'
        )}
      </ImagePreview>
    )
  }

  const handleRemoveImage = (fieldName: string) => {
    if (!challenge?.challengeId || challenge?.challengeId === '-1') {
      sendErrorNotification('Challenge ID is missing. Please save the challenge before removing images.')
      return
    }

    dispatch(
      deleteChallengeMedia({
        challengeId: challenge.challengeId,
        challengeImageType: fieldName as ImageType,
      }),
    )

    setLocalPreviews((prev) => {
      const updated = { ...prev }
      delete updated[fieldName]
      return updated
    })
  }

  const resizeImage = async (imageBlob: Blob) => {
    const options = {
      maxSizeMB: imageField.maxFileSizeMB,
    }
    try {
      const file = new File([imageBlob], 'compressed.' + fileExtension, { type: imageBlob.type })
      const compressedFile = await imageCompression(file, options)
      return compressedFile
    } catch (error) {
      console.error(error)
    }
    return false
  }

  const handleCropResult = async (imageBlob?: Blob) => {
    setCropDialogOpen(false)
    setImageInputData('')

    if (!imageBlob) {
      setCurrentImageField(null)
      setEditingPreview(null)
      return
    }

    const fieldName = (editingPreview?.name || currentImageField?.name) as ImageType
    if (!fieldName) {
      return
    }

    try {
      // Compress logo uploads, but not preview edits (already compressed via background)
      const finalBlob = currentImageField ? await resizeImage(imageBlob) : imageBlob

      if (!finalBlob) {
        sendErrorNotification('Error compressing image')
        return
      }

      if (!challenge?.challengeId || challenge?.challengeId === '-1') {
        sendErrorNotification('Challenge ID is missing. Please save the challenge before uploading images.')
        return
      }

      const { data } = await dispatch(
        requestChallengeMediaUpload({
          challengeId: challenge?.challengeId,
          challengeImageType: fieldName,
        }),
      ).unwrap()

      if (!data || !data.uploadUrl || !data.uploadHeaders) {
        throw new Error('Failed to get upload URL for background image')
      }

      // Upload to S3
      const uploadResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: finalBlob,
        headers: {
          ...data.uploadHeaders,
        },
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3')
      }

      // Set local preview immediately so the image shows without waiting for the S3 bucket
      const localUrl = URL.createObjectURL(finalBlob)
      setLocalPreviews((prev) => ({ ...prev, [fieldName]: localUrl }))

      await dispatch(
        confirmChallengeMediaUpload({
          challengeId: challenge?.challengeId,
          challengeImageType: fieldName,
          waitForCompletion: true, // Wait for the S3 upload to complete before confirming
        }),
      )

      await dispatch(
        getChallengeMedia({
          challengeId: challenge?.challengeId,
          challengeImageType: fieldName,
        }),
      ).unwrap()
    } catch (error) {
      sendErrorNotification('Error saving image')
      console.error(error)
    }

    setCurrentImageField(null)
    setEditingPreview(null)
  }

  return (
    <FormSectionContainer>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <OptInSectionTitle>
            <FormattedMessage id="challenges.optIn" />{' '}
          </OptInSectionTitle>

          <Grid sx={{ display: 'flex', flexDirection: 'column', marginTop: '24px', width: '50%' }}>
            <Grid sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: '500', marginRight: '24px' }}>
                Require player consent
              </Typography>
              <SwitchToggle
                control={control}
                name="terms.isRequired"
                label=""
                value={isRequired}
                disabled={form.formState.disabled}
              />
            </Grid>
            <Typography sx={{ color: '#8A8A8E' }}>
              Show a consent request to players before they join the challenge in the Golf GameBook app.
            </Typography>
            <Typography sx={{ color: '#8A8A8E' }}>Players must accept the opt-in text below to participate.</Typography>
          </Grid>
        </Grid>
        {isRequired && (
          <>
            <Grid item xs={6}>
              <RichTextEditor
                name="terms.description.content"
                control={control}
                label={intl.formatMessage({ id: 'challenges.optInText' })}
                placeholder={'Required'}
                maxCharactersSoftLimit={600}
                minHeight="152px"
                maxHeight="152px"
              />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={4}>
              <div>
                <ImagePreviewContainer>{renderImagePreview(imageField)}</ImagePreviewContainer>
                <h3>{imageField.label}</h3>
                <p style={{ color: '#8A8A8E', fontSize: 14, marginBottom: 16 }}>
                  <FormattedMessage id="branding.fileTypes" />: JPG, JPEG, PNG
                  <br />
                  <FormattedMessage id="branding.recommendedDimensions" />: {imageField.suggestedWidth}x
                  {imageField.suggestedHeight} px
                </p>
                <Tooltip
                  title={isUnsavedChallenge ? 'Save as draft before uploading images' : ''}
                  disableHoverListener={!isUnsavedChallenge}
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#222A35',
                        color: '#FFF',
                        fontSize: '16px',
                        padding: '20px',
                        fontWeight: '400',
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#222A35',
                      },
                    },
                  }}
                >
                  <span>
                    <FilePicker
                      name={imageField.name}
                      id={imageField.name}
                      onChange={onFilePickerChange}
                      accept="image/jpeg, image/jpg, image/png"
                      disabled={isUnsavedChallenge || form.formState.disabled}
                    >
                      <Button
                        variant="outlined"
                        component="span"
                        style={{ textTransform: 'none' }}
                        disabled={isUnsavedChallenge || form.formState.disabled}
                        sx={{
                          padding: '10px 16px',
                          background: 'white',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          borderRadius: '12px',
                          border: '1px solid var(--neutral-300, #d4d4d4)',
                          color: 'var(--neutral-700, #404040)',
                          fontFamily: 'var(--family-sans, Söhne)',
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '20px',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: 'var(--neutral-300, #d4d4d4)',
                            background: 'white',
                          },
                        }}
                      >
                        <FormattedMessage id="strings.uploadFile" />
                        <UploadCloudIcon style={{ marginLeft: 8 }} />
                      </Button>
                    </FilePicker>
                  </span>
                </Tooltip>
              </div>
            </Grid>
            <CropImageDialog
              open={cropDialogOpen}
              src={imageInputData}
              onClose={handleCropResult}
              aspect={imageField.suggestedWidth / imageField.suggestedHeight}
              outputMaxWidth={imageField.suggestedWidth * 3}
            />
          </>
        )}
      </Grid>
    </FormSectionContainer>
  )
}

const OptInSectionTitle = styled.h2`
  color: ${(props) => props.theme.palette.primary.main};
`

const ImagePreviewContainer = styled.div`
  display: flex;
  height: 100%;
  margin: 1rem 0;
`

const ImagePreview = styled.div<{ $hasImage: boolean; $circular?: boolean; $aspectRatio: number }>`
  border: 2px dashed ${(props) => (props.$hasImage ? '#373D40' : '#BBBBBD')};
  border-radius: ${(props) => (props.$circular ? '50%' : '16px')};
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 4px;
  box-sizing: content-box;
  color: #8a8a8e;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 15px;
  aspect-ratio: ${(props) => props.$aspectRatio};

  & img {
    height: 100%;
    border-radius: ${(props) => (props.$circular ? '50%' : '12px')};
  }
`

const RemoveButton = styled.div`
  position: absolute;
  top: -11px;
  right: -11px;
  cursor: pointer;
`
